import { taskIsSleeping } from "./taskAttention"
/**
 * TaskModel - Observable wrapper for Task
 *
 * Provides derived state and actions for tasks.
 * Models compute from the active task projection - no task data is cached here.
 */

import { makeAutoObservable, reaction, runInAction } from "mobx"
import { track } from "../analytics"
import { DEFAULT_MODEL, getDefaultModelForHarness, harnessIdForProvider, isHarnessId, resolveModelForHarness } from "../constants"
import { requestNestMethod } from "../nest/client"
import type { Session, SessionEvent, SessionUsage } from "../nest/generated"
import { type GitSummaryResponse, gitApi } from "../nestAPIs/git"
import type { TaskDelegateTarget, TaskPullRequestAutomation, TaskWorktreeRecovery } from "../persistence/records"
import { computeTaskUsage } from "../persistence/taskStatsUtils"
import { buildTaskThreadMarkdown } from "../prompts/taskThreadMarkdown"
import { type TaskThreadFormat, type TaskThreadJson, buildTaskThreadJson, buildTaskThreadXml } from "../prompts/taskThreadSerializer"
import type { HarnessId, HarnessStreamEvent } from "../providerEvents/harnessEventTypes"
import { classifyProviderUserMessage } from "../providerEvents/userMessages"
import type { ActionEvent, CodeEvent, QueuedTurn, Task, TaskProviderSessionReference } from "../types"
import { filterGitSummary } from "../utils/gitTestFileFilter"
import { requestWildLarkMethod, type WildLarkTaskHandoffParams, type WildLarkTaskSessionsListResult } from "../wildlark/client"
import { ActionEventModel, type EventModel, SetupEnvironmentEventModel, SnapshotEventModel } from "./EventModel"
import {
    type SessionThreadItem,
    type SessionThreadReference,
    SESSION_THREAD_HISTORY_BYTE_LIMIT,
    SESSION_THREAD_HISTORY_EVENT_LIMIT,
    buildSessionThreadItemsFromSessionEvents,
    primeSessionThread,
    trackSessionThreadPrime,
} from "./SessionThreadModel"
import { continuationProviderSession } from "./taskRecordAdapter"
import { ChangesManager } from "./managers/ChangesManager"
import { ContentSearchManager } from "./managers/ContentSearchManager"
import { FileBrowserManager } from "./managers/FileBrowserManager"
import { InputManager } from "./managers/InputManager"
import { ACTIVE_TURN_STATUSES } from "./managers/RuntimeManager"
import { SdkCapabilitiesManager } from "./managers/SdkCapabilitiesManager"
import { TrayManager } from "./managers/TrayManager"
import type { CodeStore } from "./store"
import { loadTaskCheckoutSummaries, type TaskCheckoutSummary, type TaskCheckoutSummaryReadStats } from "./taskCheckouts"
import {
    getTaskDirectory,
    inspectTaskDirectoryPath,
    resolveTaskDirectoryView,
    taskDirectoryForSelection,
    type TaskDirectory,
    type TaskCommitTarget,
    type TaskDirectoryPurpose,
    type TaskDirectoryView,
} from "./taskDirectory"

import type { ThinkingLevel } from "../agentTarget"
export type { ThinkingLevel } from "../agentTarget"

interface DynamicSessionReference {
    harnessId: HarnessId
    sessionId: string
    providerSessionId?: string
    cwd?: string
    model?: string
}

interface DynamicSessionProjection {
    reference: DynamicSessionReference
    session?: Session
    events: DynamicHarnessStreamEvent[]
    sessionEvents: SessionEvent[]
    truncated?: boolean
}

interface TaskCommitRange extends TaskCommitTarget {
    upstreamCommit: string
}

async function resolveTaskCommitRange(target: TaskCommitTarget): Promise<TaskCommitRange | null> {
    const { defaultBranchSha } = await gitApi.listBranches({
        ...target.directory.context,
        directory: target.directory.path,
        includeRemote: false,
    })
    return defaultBranchSha ? { ...target, upstreamCommit: defaultBranchSha } : null
}

type DynamicHarnessStreamEvent = HarnessStreamEvent & { turnId?: string }

interface ExecutionSelection {
    thinking?: ThinkingLevel
    harnessId: HarnessId
    model?: string
    fastMode?: boolean
}

interface PendingModelSelection {
    clientRequestId: string
    harnessId: HarnessId
    modelId: string
    turnId?: string
}

interface LoadedSessionEvents {
    events: DynamicHarnessStreamEvent[]
    sessionEvents: SessionEvent[]
    truncated: boolean
}

interface LoadedSessionUsage {
    sessionId: string
    usage?: SessionUsage
    error?: unknown
}

export interface TaskStats {
    totalCostUsd: number
    costAvailable: boolean
    durationMs: number
    inputTokens: number
    outputTokens: number
    loading: boolean
    error: string | null
    source: "events" | "nest"
}

export interface TaskSessionDebugInfo {
    thinking?: ThinkingLevel
    key: string
    harnessId: HarnessId
    role?: string
    label?: string
    parentSessionId?: string
    providerId?: string
    providerSessionId?: string
    nestSessionId?: string
    sessionFile?: string
    sessionRoot?: string
    model?: string
    mode?: string
    cwd?: string
    worktreeId?: string
    executionDirectory?: string
    activeTurnId?: string
    queuedTurnIds?: string[]
    eventCount?: number
    lastInputPreview?: string
    lastEventPreview?: string
    lastTurnId?: string
    createdAt?: string
    updatedAt?: string
    lastUsedAt?: string
    loadedEventCount?: number
    loadedEventTruncated?: boolean
    status?: string
    current?: boolean
    error?: string
    usage?: TaskSessionUsageSummary
}

export interface TaskSessionUsageSummary {
    costUsd: number
    costAvailable: boolean
    durationMs: number
    inputTokens: number
    outputTokens: number
}

function harnessIdFromProvider(providerId: string | undefined): HarnessId {
    return harnessIdForProvider(providerId) ?? "claude-code"
}

function isNestSessionId(sessionId: string | undefined): sessionId is string {
    return typeof sessionId === "string" && sessionId.startsWith("ses_")
}

function costFromSessionUsage(usage: SessionUsage): number {
    return usage.reportedCostUsd ?? usage.estimatedCostUsd ?? 0
}

function hasSessionUsageCost(usage: SessionUsage): boolean {
    return usage.source.cost !== "unavailable" && (usage.reportedCostUsd !== undefined || usage.estimatedCostUsd !== undefined)
}

function hasSessionTokenUsage(usage: SessionUsage): boolean {
    return usage.source.tokens !== "unavailable" || usage.inputTokens > 0 || usage.outputTokens > 0
}

function actionStatusFromSession(status: string | undefined): ActionEvent["status"] {
    switch (status) {
        case "running":
        case "queued":
        case "starting":
            return "in_progress"
        case "failed":
        case "error":
            return "error"
        case "stopped":
        case "cancelled":
            return "stopped"
        default:
            return "completed"
    }
}

function streamEventFromSessionEvent(item: SessionEvent, harnessId: HarnessId, executionId: string): DynamicHarnessStreamEvent {
    const event: DynamicHarnessStreamEvent = {
        id: item.eventId,
        type: "raw_message",
        direction: "execution",
        executionId,
        harnessId,
        message: item.raw,
    }
    if (item.turnId) event.turnId = item.turnId
    return event
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function stringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}

function nonEmptyString(value: string | undefined): string | undefined {
    const trimmed = value?.trim()
    return trimmed ? trimmed : undefined
}

function textFromContent(value: unknown): string | undefined {
    if (typeof value === "string") return nonEmptyString(value)
    if (!Array.isArray(value)) return undefined
    const text = value
        .map((block) => {
            if (typeof block === "string") return block
            const record = recordValue(block)
            if (!record) return ""
            const type = stringValue(record.type)
            if (type && type !== "text" && type !== "input_text" && type !== "output_text") return ""
            return stringValue(record.text) ?? stringValue(record.content) ?? ""
        })
        .join("")
    return nonEmptyString(text)
}

function promptTextFromRecord(record: Record<string, unknown>): string | undefined {
    return (
        nonEmptyString(stringValue(record.message)) ??
        nonEmptyString(stringValue(record.text)) ??
        nonEmptyString(stringValue(record.input)) ??
        textFromContent(record.content)
    )
}

function codexUserPromptFromRaw(raw: unknown): string | undefined {
    const record = recordValue(raw)
    if (!record) return undefined
    const type = stringValue(record.type)
    const payload = recordValue(record.payload)

    if (type === "event_msg" && payload && stringValue(payload.type) === "user_message") {
        return promptTextFromRecord(payload)
    }

    if (type === "response_item" && payload && stringValue(payload.type) === "message" && stringValue(payload.role) === "user") {
        return promptTextFromRecord(payload)
    }

    if (type === "user_message") return promptTextFromRecord(record)
    if (type === "message" && stringValue(record.role) === "user") return promptTextFromRecord(record)
    return undefined
}

function claudeUserPromptFromRaw(raw: unknown): string | undefined {
    const record = recordValue(raw)
    if (!record) return undefined
    const type = stringValue(record.type)

    if (type === "user") {
        const message = recordValue(record.message)
        return message ? promptTextFromRecord(message) : promptTextFromRecord(record)
    }

    if (type === "message" && stringValue(record.role) === "user") {
        return promptTextFromRecord(record)
    }

    return undefined
}

function userPromptFromProviderRaw(raw: unknown, harnessId: HarnessId): string | undefined {
    if (harnessId === "codex") return codexUserPromptFromRaw(raw)
    if (harnessId === "grok-build" || harnessId === "opencode") {
        const classified = classifyProviderUserMessage(harnessId, raw)
        return classified?.parts
            .filter((part) => part.type === "text")
            .map((part) => part.text)
            .join("\n")
    }
    return claudeUserPromptFromRaw(raw)
}

function normalizePromptText(value: string | undefined): string | undefined {
    const normalized = value?.replace(/\s+/g, " ").trim()
    return normalized ? normalized : undefined
}

function promptsMatch(providerPrompt: string | undefined, actionPrompt: string | undefined): boolean {
    const provider = normalizePromptText(providerPrompt)
    const action = normalizePromptText(actionPrompt)
    if (!provider || !action) return false
    if (provider === action) return true
    if (action.length >= 20 && provider.includes(action)) return true
    if (provider.length >= 20 && action.includes(provider)) return true
    return false
}

function actionEventsBySession(task: Task): Map<string, ActionEvent[]> {
    const result = new Map<string, ActionEvent[]>()
    for (const event of task.events) {
        if (event.type !== "action" || !event.execution.sessionId) continue
        const events = result.get(event.execution.sessionId) ?? []
        events.push(event)
        result.set(event.execution.sessionId, events)
    }
    return result
}

function actionBoundaryIndex(projection: DynamicSessionProjection, action: ActionEvent, searchStart: number): number | undefined {
    for (let index = searchStart; index < projection.events.length; index += 1) {
        const event = projection.events[index]
        if (!event || event.type !== "raw_message") continue
        if (promptsMatch(userPromptFromProviderRaw(event.message, projection.reference.harnessId), action.userInput)) {
            return index
        }
    }
    return undefined
}

function actionBoundaryIndexes(projection: DynamicSessionProjection, sessionActions: ActionEvent[]): Array<number | undefined> {
    const indexes: Array<number | undefined> = []
    let searchStart = 0
    for (const action of sessionActions) {
        const index = actionBoundaryIndex(projection, action, searchStart)
        indexes.push(index)
        if (index !== undefined) searchStart = index + 1
    }
    return indexes
}

function eventsForActionByTurnId(projection: DynamicSessionProjection, action: ActionEvent): DynamicHarnessStreamEvent[] {
    const turnIds = new Set([action.id, action.execution.executionId].filter((value): value is string => !!value))
    if (turnIds.size === 0) return []
    return projection.events.filter((event) => !!event.turnId && turnIds.has(event.turnId))
}

function eventsForActionByPromptBoundary(
    projection: DynamicSessionProjection,
    action: ActionEvent,
    sessionActions: ActionEvent[]
): DynamicHarnessStreamEvent[] {
    const actionIndex = sessionActions.findIndex((candidate) => candidate.id === action.id)
    if (actionIndex < 0) return projection.events

    const boundaries = actionBoundaryIndexes(projection, sessionActions)
    if (!boundaries.some((index) => index !== undefined)) {
        return actionIndex === 0 ? projection.events : []
    }

    const start = actionIndex === 0 ? 0 : boundaries[actionIndex]
    if (start === undefined) return []
    const nextStart = boundaries.slice(actionIndex + 1).find((index): index is number => index !== undefined)
    return projection.events.slice(start, nextStart ?? projection.events.length)
}

function eventsForAction(projection: DynamicSessionProjection, action: ActionEvent, sessionActions: ActionEvent[]): DynamicHarnessStreamEvent[] {
    const turnEvents = eventsForActionByTurnId(projection, action)
    if (turnEvents.length > 0) return turnEvents
    if (sessionActions.length <= 1) return projection.events
    return eventsForActionByPromptBoundary(projection, action, sessionActions)
}

function syntheticActionFromProjection(task: Task, projection: DynamicSessionProjection): ActionEvent {
    const session = projection.session
    const reference = projection.reference
    const createdAt = session?.createdAt ?? task.createdAt
    const updatedAt = session?.updatedAt ?? task.updatedAt
    const model = session?.model ?? reference.model
    return {
        id: `nest-session:${reference.sessionId}`,
        type: "action",
        status: actionStatusFromSession(session?.status),
        createdAt,
        completedAt: actionStatusFromSession(session?.status) === "in_progress" ? undefined : updatedAt,
        userInput: session?.lastInputPreview ?? task.description,
        execution: {
            harnessId: reference.harnessId,
            executionId: reference.sessionId,
            sessionId: reference.sessionId,
            modelId: model,
            events: projection.events,
        },
        source: {
            type: "do",
            userLabel: "Session",
        },
        includesCommentIds: [],
    }
}

function userInputFromThreadItem(item: Extract<SessionThreadItem, { type: "user" }>): string {
    return item.parts
        .map((part) => {
            if (part.type === "text") return part.text
            if (part.type === "image") return "[Image attachment]"
            return ""
        })
        .filter(Boolean)
        .join("\n\n")
}

function syntheticActionsFromProjection(task: Task, projection: DynamicSessionProjection): ActionEvent[] {
    const items = buildSessionThreadItemsFromSessionEvents(projection.reference, projection.sessionEvents)
    const actions: ActionEvent[] = []
    let pendingEvents: HarnessStreamEvent[] = []
    let currentAction: ActionEvent | null = null

    const finishCurrentAction = () => {
        if (!currentAction) return
        actions.push(currentAction)
        currentAction = null
    }

    for (const item of items) {
        if (item.type === "user") {
            finishCurrentAction()
            const createdAt = item.timestamp ?? projection.session?.createdAt ?? task.createdAt
            currentAction = {
                id: `${projection.reference.sessionId}:export:${item.id}`,
                type: "action",
                status: "completed",
                createdAt,
                completedAt: createdAt,
                userInput: userInputFromThreadItem(item),
                execution: {
                    harnessId: projection.reference.harnessId,
                    executionId: item.order.turnId ?? item.id,
                    sessionId: projection.reference.sessionId,
                    modelId: projection.session?.model ?? projection.reference.model,
                    events: pendingEvents,
                },
                source: { type: "do", userLabel: "Do" },
                includesCommentIds: [],
                result: { success: true },
            }
            pendingEvents = []
            continue
        }

        if (item.type === "provider_events") {
            if (currentAction) currentAction.execution.events.push(...item.events)
            else pendingEvents.push(...item.events)
        }
    }

    finishCurrentAction()
    if (actions.length === 0) return []

    const lastIndex = actions.length - 1
    return actions.map((action, index) => {
        if (index !== lastIndex) return action
        const status = actionStatusFromSession(projection.session?.status)
        return {
            ...action,
            status,
            completedAt: status === "in_progress" ? undefined : (projection.session?.updatedAt ?? action.completedAt),
        }
    })
}

function finalizeLoadedSessionEvents(reference: DynamicSessionReference, sessionEvents: SessionEvent[], truncated: boolean): LoadedSessionEvents {
    const sortedSessionEvents = sessionEvents.every((event) => Number.isFinite(event.sequence))
        ? [...sessionEvents].sort((left, right) => left.sequence - right.sequence || left.eventId.localeCompare(right.eventId))
        : [...sessionEvents]
    return {
        sessionEvents: sortedSessionEvents,
        events: sortedSessionEvents.map((event) => streamEventFromSessionEvent(event, reference.harnessId, reference.sessionId)),
        truncated,
    }
}

export class TaskModel {
    gitStatus: GitSummaryResponse | null = null
    directoryView: TaskDirectoryView | null = null
    taskCommitCount: number | null = null
    model: string = DEFAULT_MODEL
    thinking: ThinkingLevel = "max"
    fastMode = false
    harnessId: HarnessId = "claude-code"
    private modelSelectionIsExplicit = false
    private pendingModelSelection: PendingModelSelection | null = null
    private directoryRefreshPromise: Promise<void> | null = null
    private directoryRefreshRequested = false
    private directorySelectionRefresh: { path: string | undefined; promise: Promise<void> } | null = null
    private disposed = false
    private taskCommitCountSourceKey: string | null = null
    private taskCommitCountHeadCommit: string | null = null
    private trackedAgentGitTarget = false
    private _inputManager: InputManager | null = null
    private _trayManager: TrayManager | null = null
    private _fileBrowser: FileBrowserManager | null = null
    private _contentSearch: ContentSearchManager | null = null
    private _sdkCapabilities: SdkCapabilitiesManager | null = null
    private _changes: ChangesManager | null = null
    private _eventModelCache = new Map<string, EventModel>()
    private _dynamicEventsBySession = new Map<string, DynamicSessionProjection>()
    dynamicActivityLoading = false
    dynamicActivityReady = false
    dynamicActivityError: string | null = null
    private _dynamicActivityLoadPromise: Promise<void> | null = null
    private _nestUsageBySession = new Map<string, SessionUsage>()
    nestUsageLoading = false
    nestUsageError: string | null = null
    private _nestUsageLoadKey = ""
    private _nestUsageLoadPromise: Promise<void> | null = null
    private _nestUsageReadPromises = new Map<string, Promise<LoadedSessionUsage>>()
    taskSessionManifest: WildLarkTaskSessionsListResult | null = null
    taskSessionManifestLoading = false
    taskSessionManifestError: string | null = null
    checkoutSummaries: TaskCheckoutSummary[] = []
    checkoutSummaryBaseBranch: string | null = null
    checkoutSummaryReads: TaskCheckoutSummaryReadStats | null = null
    checkoutSummariesError: string | null = null
    private taskSessionManifestLoadKey = ""
    private taskSessionManifestLoadPromise: Promise<void> | null = null
    private taskSessionManifestRefreshRequested = false
    private checkoutSummariesLoadPromise: Promise<void> | null = null
    private checkoutSummariesRequested = false
    private checkoutSummariesRefreshRequested = false
    private disposers: Array<() => void> = []

    constructor(
        private store: CodeStore,
        public readonly taskId: string
    ) {
        makeAutoObservable(this, {
            taskId: false,
            _eventModelCache: false,
            _nestUsageReadPromises: false,
            taskSessionManifestLoadPromise: false,
            taskSessionManifestRefreshRequested: false,
            checkoutSummariesLoadPromise: false,
            checkoutSummariesRequested: false,
            checkoutSummariesRefreshRequested: false,
            directoryRefreshPromise: false,
            directoryRefreshRequested: false,
            directorySelectionRefresh: false,
            disposed: false,
            taskCommitCountSourceKey: false,
            taskCommitCountHeadCommit: false,
            getDirectory: false,
        } as never)

        // Provider activity may create, remove, or modify the task's effective checkout.
        this.disposers.push(
            this.store.execution.onAfterEvent((eventTaskId, _eventType, _success, turnId) => {
                if (eventTaskId === this.taskId) {
                    this.finishModelSubmission(turnId)
                    const directoryRefresh = this.refreshDirectoryView()
                    let manifestRefresh: Promise<void> | null = null
                    if (this.taskSessionManifest || this.taskSessionManifestLoadPromise) {
                        manifestRefresh = this.loadTaskSessionManifest(true)
                    }
                    if (this.checkoutSummariesRequested) {
                        void Promise.all([directoryRefresh, manifestRefresh ?? Promise.resolve()]).then(() => this.refreshCheckoutSummaries())
                    }
                }
            })
        )
        this.disposers.push(
            reaction(
                () => this.store.productState?.tasks.get(this.taskId)?.directorySelection?.path,
                (path) => {
                    const promise = this.refreshDirectoryView()
                    this.directorySelectionRefresh = { path, promise }
                }
            )
        )

        this.disposers.push(
            reaction(
                () => this.store.productState?.tasks.get(this.taskId)?.activeSessionId,
                () => {
                    this.pendingModelSelection = null
                    this.modelSelectionIsExplicit = false
                    this.initializeExecutionSelectionFromHistory()
                }
            )
        )
        this.initializeExecutionSelectionFromHistory()
    }

    /**
     * Clean up all subscriptions. Call when TaskModel is no longer needed.
     */
    dispose(): void {
        this.disposed = true
        this.directoryRefreshRequested = false
        this.directorySelectionRefresh = null
        this.taskSessionManifestRefreshRequested = false
        for (const disposer of this.disposers) {
            disposer()
        }
        this.disposers = []
        this._nestUsageReadPromises.clear()
        this.checkoutSummariesLoadPromise = null
        this.checkoutSummariesRefreshRequested = false
        this._changes?.dispose()
    }

    // === Input manager ===

    get input(): InputManager {
        if (!this._inputManager) {
            const editorManager = this.store.smartEditors.getManager(`task-${this.taskId}`, this.workspaceId)
            this._inputManager = new InputManager(this.store, this.taskId, editorManager)
        }
        return this._inputManager
    }

    // === Tray manager ===

    get tray(): TrayManager {
        if (!this._trayManager) {
            this._trayManager = new TrayManager(this.store, this)
        }
        return this._trayManager
    }

    // === File browser ===

    get fileBrowser(): FileBrowserManager {
        if (!this._fileBrowser) {
            const directory = this.getDirectory("working")
            this._fileBrowser = new FileBrowserManager()
            if (directory) this._fileBrowser.setDirectory(directory.path, directory.context)
        }
        return this._fileBrowser
    }

    // === Content search ===

    get contentSearch(): ContentSearchManager {
        if (!this._contentSearch) {
            const directory = this.getDirectory("working")
            this._contentSearch = new ContentSearchManager()
            if (directory) this._contentSearch.setDirectory(directory.path, directory.context)
        }
        return this._contentSearch
    }

    // === SDK capabilities ===

    get sdkCapabilities(): SdkCapabilitiesManager {
        if (!this._sdkCapabilities) {
            this._sdkCapabilities = new SdkCapabilitiesManager()
        }
        return this._sdkCapabilities
    }

    // === Changes ===

    get changes(): ChangesManager {
        if (!this._changes) {
            this._changes = new ChangesManager(this, this.store)
        }
        return this._changes
    }

    get displayGitStatus(): GitSummaryResponse | null {
        return this.gitStatus ? filterGitSummary(this.gitStatus, !this.store.showTestFiles) : null
    }

    // === Model & Harness ===

    get delegateTarget(): TaskDelegateTarget | null {
        return this.store.productState?.tasks.get(this.taskId)?.delegateTarget ?? null
    }

    async setDelegateTarget(delegateTarget: TaskDelegateTarget | null): Promise<void> {
        const productState = this.store.productState
        const record = productState?.tasks.get(this.taskId)
        if (!productState || !record) throw new Error("This task is no longer available")
        const sessionId = this.primarySessionThreadReference?.sessionId
        if (!sessionId) throw new Error("This task has no main agent")
        try {
            await requestWildLarkMethod("wildlark.task.delegate.set", { taskId: this.taskId, workspaceId: this.workspaceId, sessionId, target: delegateTarget })
        } finally {
            await productState.refreshTask(this.taskId, this.workspaceId)
        }
    }

    setModel(modelId: string): void {
        this.pendingModelSelection = null
        this.model = this.normalizeModelForHarness(modelId, this.harnessId)
        this.modelSelectionIsExplicit = true
    }

    beginModelSubmission(clientRequestId: string): void {
        if (!this.modelSelectionIsExplicit) return
        this.pendingModelSelection = {
            clientRequestId,
            harnessId: this.harnessId,
            modelId: this.model,
        }
        this.modelSelectionIsExplicit = false
    }

    acceptModelSubmission(clientRequestId: string, turnId: string, status: string): void {
        const pendingSelection = this.pendingModelSelection
        if (!pendingSelection || pendingSelection.clientRequestId !== clientRequestId) return

        const observedRuntime = this.store.runtimes.runtimesById.get(turnId)
        if (!ACTIVE_TURN_STATUSES.has(status) || (observedRuntime && !ACTIVE_TURN_STATUSES.has(observedRuntime.status))) {
            this.pendingModelSelection = null
            return
        }

        this.pendingModelSelection = { ...pendingSelection, turnId }
    }

    rejectModelSubmission(clientRequestId: string): void {
        if (this.pendingModelSelection?.clientRequestId === clientRequestId) {
            this.pendingModelSelection = null
        }
    }

    finishModelSubmission(turnId: string | undefined): void {
        if (turnId && this.pendingModelSelection?.turnId === turnId) {
            this.pendingModelSelection = null
        }
    }

    reconcileMissingRuntimeTurns(turnIds: readonly string[]): void {
        for (const turnId of turnIds) this.finishModelSubmission(turnId)
        if (this.taskSessionManifest || this.taskSessionManifestLoadPromise) void this.loadTaskSessionManifest(true)
    }

    setThinking(level: ThinkingLevel): void {
        this.thinking = level
    }

    setFastMode(enabled: boolean): void {
        this.fastMode = enabled
    }

    setHarnessId(id: HarnessId): void {
        if (this.hasActionHistory) return
        if (id === this.harnessId) return
        this.harnessId = id
        this.modelSelectionIsExplicit = false
        this.pendingModelSelection = null
        // Reset model to the new harness's default
        this.model = getDefaultModelForHarness(id)
    }

    syncExecutionSelectionFromSession(session: Session): void {
        const reference = this.primarySessionThreadReference
        if (!reference || session.sessionId !== reference.sessionId || !session.model) return

        const sessionModel = this.normalizeModelForHarness(session.model, reference.harnessId)
        const pendingSelection = this.pendingModelSelection
        this.harnessId = reference.harnessId
        if (pendingSelection?.harnessId === reference.harnessId && pendingSelection.modelId !== sessionModel) return

        this.modelSelectionIsExplicit = false
        this.pendingModelSelection = null
        this.model = sessionModel
    }

    private get hasActionHistory(): boolean {
        const task = this.task
        if (!task) return false
        if (task.events.some((event) => event.type === "action")) return true
        if ((task.providerSessions?.length ?? 0) > 0) return true
        return Object.keys(task.sessionIds).length > 0
    }

    private initializeExecutionSelectionFromHistory(): void {
        const events = this.task?.activeSessionId ? [] : (this.task?.events ?? [])
        for (let i = events.length - 1; i >= 0; i--) {
            const event = events[i]
            if (event.type !== "action") continue

            // v1 compat: pre-harness tasks stored `type` instead of `harnessId`
            const harnessId: HarnessId =
                event.execution.harnessId ?? ((event.execution as unknown as Record<string, unknown>).type as HarnessId) ?? "claude-code"
            this.harnessId = harnessId
            const persistedModelId = event.execution.modelId
            this.model = persistedModelId ? this.normalizeModelForHarness(persistedModelId, harnessId) : getDefaultModelForHarness(harnessId)
            this.thinking = event.execution.thinking ?? "max"
            this.fastMode = event.execution.fastMode ?? false
            return
        }

        const migratedSelection = this.executionSelectionFromProviderSessions()
        if (!migratedSelection) return

        this.harnessId = migratedSelection.harnessId
        this.model = migratedSelection.model
            ? this.normalizeModelForHarness(migratedSelection.model, migratedSelection.harnessId)
            : getDefaultModelForHarness(migratedSelection.harnessId)
        this.thinking = migratedSelection.thinking ?? "max"
        this.fastMode = migratedSelection.fastMode ?? false
    }

    private executionSelectionFromProviderSessions(): ExecutionSelection | null {
        const task = this.task
        if (!task) return null

        const continuation = this.continuationProviderSessionForTask(task)
        if (continuation) {
            return {
                harnessId: isHarnessId(continuation.harnessId) ? continuation.harnessId : harnessIdFromProvider(continuation.providerId),
                model: continuation.model,
                thinking: continuation.thinking,
            }
        }

        const sessionIds = Object.keys(task.sessionIds)
        const rawHarnessId = sessionIds.find((id) => isHarnessId(id)) ?? sessionIds.at(0)
        if (!rawHarnessId) return null
        return {
            harnessId: isHarnessId(rawHarnessId) ? rawHarnessId : harnessIdFromProvider(rawHarnessId),
        }
    }

    private normalizeModelForHarness(modelId: string, harnessId: HarnessId): string {
        return resolveModelForHarness(modelId, harnessId)
    }

    private get task(): Task | undefined {
        return this.store.tasks.getTask(this.taskId) ?? undefined
    }

    // === Raw accessors ===

    get exists(): boolean {
        return !!this.task
    }

    get id(): string {
        return this.taskId
    }

    get agentGitContext(): Task["agentGitContext"] {
        return this.task?.agentGitContext
    }

    get directorySelection(): Task["directorySelection"] {
        return this.task?.directorySelection
    }

    get title(): string {
        return this.task?.title ?? ""
    }

    get isClosed(): boolean {
        return this.task?.closed ?? false
    }

    get queuePriority(): Task["queuePriority"] {
        return this.task?.queuePriority
    }

    get slug(): string {
        return this.task?.slug ?? ""
    }

    get description(): string {
        return this.task?.description ?? ""
    }

    get repoId(): string {
        return this.task?.repoId ?? ""
    }

    /** Alias for repoId - prefer this in new code for consistency with routing */
    get workspaceId(): string {
        return this.repoId
    }

    get createdAt(): string {
        return this.task?.createdAt ?? ""
    }

    get sessionIds(): Record<string, string> {
        return this.task?.sessionIds ?? {}
    }

    handoffSubmitting = false

    get pendingHandoff() {
        return this.store.productState?.tasks.get(this.taskId)?.handoff
    }

    async handoffTo(params: Omit<WildLarkTaskHandoffParams, "taskId" | "workspaceId" | "options">): Promise<void> {
        if (this.input.queuedTurns.length > 0) throw new Error("Run or cancel queued messages before handing off")
        await this.runHandoffMutation(async () => {
            const result = await requestWildLarkMethod("wildlark.task.handoff", {
                ...params,
                taskId: this.taskId,
                workspaceId: this.workspaceId,
                options: { permissionMode: this.store.personalSettingsStore?.settings.current.permissionMode ?? "yolo" },
            })
            if (result.turn.status === "unknown")
                throw new Error(
                    "The lead changed, but startup was not confirmed. Inspect the new session before continuing; this retry did not start duplicate work."
                )
            if (result.warnings?.length) throw new Error(`Handoff started, with a recording issue: ${result.warnings.join("; ")}`)
        })
    }

    async cancelHandoff(): Promise<void> {
        const pending = this.pendingHandoff
        if (!pending) return
        await this.runHandoffMutation(async () => {
            await requestWildLarkMethod("wildlark.task.handoff.cancel", {
                taskId: this.taskId,
                workspaceId: this.workspaceId,
                sessionId: pending.sessionId,
                clientRequestId: pending.clientRequestId,
            })
        })
    }

    private async runHandoffMutation(operation: () => Promise<void>): Promise<void> {
        if (this.handoffSubmitting) throw new Error("A handoff operation is already in progress")
        this.handoffSubmitting = true
        try {
            await operation()
        } finally {
            try {
                await this.store.productState?.refreshTask(this.taskId, this.workspaceId)
            } finally {
                runInAction(() => {
                    this.handoffSubmitting = false
                    this.pendingModelSelection = null
                    this.modelSelectionIsExplicit = false
                    this.initializeExecutionSelectionFromHistory()
                })
                void this.loadTaskSessionManifest(true)
            }
        }
    }

    get providerSessions(): TaskProviderSessionReference[] {
        return this.task?.providerSessions ?? []
    }

    get shouldRenderSessionThread(): boolean {
        return this.primarySessionThreadReference !== null
    }

    get primarySessionThreadReference(): SessionThreadReference | null {
        const task = this.task
        if (!task) return null

        if (task.activeSessionId) return this.sessionThreadReferenceForSessionId(task.activeSessionId)

        const latestActionSessionId = [...task.events].reverse().find((event): event is ActionEvent => event.type === "action" && !!event.execution.sessionId)
            ?.execution.sessionId
        if (latestActionSessionId) {
            return this.sessionThreadReferenceForSessionId(latestActionSessionId)
        }

        const providerSession = this.continuationProviderSessionForTask(task)
        if (providerSession?.nestSessionId) {
            return this.sessionThreadReferenceForProviderSession(providerSession)
        }

        const [rawHarnessId, sessionId] = Object.entries(task.sessionIds).at(0) ?? []
        if (!rawHarnessId || !sessionId) return null
        return {
            sessionId,
            harnessId: isHarnessId(rawHarnessId) ? rawHarnessId : harnessIdFromProvider(rawHarnessId),
            workspaceId: task.repoId,
        }
    }

    private continuationProviderSessionForTask(task: Task): TaskProviderSessionReference | null {
        return continuationProviderSession(task.providerSessions ?? [], task.activeSessionId)
    }

    get sessionDebugInfo(): TaskSessionDebugInfo[] {
        const task = this.task
        if (!task) return []

        const result = new Map<string, TaskSessionDebugInfo>()
        for (const [index, session] of (this.taskSessionManifest?.sessions ?? []).entries()) {
            const harnessId = harnessIdFromProvider(session.providerId)
            const key = session.sessionId ?? session.providerSessionId ?? `manifest:${index}`
            result.set(key, {
                key,
                harnessId,
                role: session.role,
                label: session.label,
                parentSessionId: session.parentSessionId,
                providerId: session.providerId,
                providerSessionId: session.providerSessionId,
                nestSessionId: session.sessionId,
                sessionFile: session.sessionFile,
                sessionRoot: session.sessionRoot,
                model: session.model,
                thinking: session.thinking,
                mode: session.mode,
                cwd: session.cwd,
                worktreeId: session.worktreeId,
                executionDirectory: session.executionDirectory,
                activeTurnId: session.activeTurnId,
                queuedTurnIds: session.queuedTurnIds,
                eventCount: session.eventCount,
                lastInputPreview: session.lastInputPreview,
                lastEventPreview: session.lastEventPreview,
                lastTurnId: session.lastTurnId,
                createdAt: session.createdAt,
                updatedAt: session.updatedAt,
                status: session.status,
                current: session.current,
                error: session.error,
            })
        }
        for (const providerSession of task.providerSessions ?? []) {
            const harnessId = isHarnessId(providerSession.harnessId) ? providerSession.harnessId : harnessIdFromProvider(providerSession.providerId)
            const key = providerSession.nestSessionId ?? `${providerSession.providerId}:${providerSession.providerSessionId}`
            const manifest = result.get(key)
            const projection = providerSession.nestSessionId ? this._dynamicEventsBySession.get(providerSession.nestSessionId) : undefined
            const recordedProviderSessionId =
                providerSession.providerSessionId === providerSession.nestSessionId ? undefined : providerSession.providerSessionId
            result.set(key, {
                ...manifest,
                key,
                harnessId,
                role: manifest?.role ?? providerSession.role,
                label: manifest?.label ?? providerSession.label,
                parentSessionId: manifest?.parentSessionId ?? providerSession.parentSessionId,
                providerId: projection?.session?.providerId ?? manifest?.providerId ?? providerSession.providerId,
                providerSessionId: projection?.session?.providerSessionId ?? manifest?.providerSessionId ?? recordedProviderSessionId,
                nestSessionId: providerSession.nestSessionId,
                sessionFile: projection?.session?.sessionFile ?? manifest?.sessionFile,
                sessionRoot: projection?.session?.sessionRoot ?? manifest?.sessionRoot,
                model: projection?.session?.model ?? manifest?.model ?? providerSession.model,
                thinking: manifest?.thinking ?? providerSession.thinking,
                mode: manifest?.mode ?? providerSession.mode,
                cwd: projection?.session?.cwd ?? manifest?.cwd ?? providerSession.cwd,
                worktreeId: projection?.session?.worktreeId ?? manifest?.worktreeId,
                lastUsedAt: providerSession.lastUsedAt,
                loadedEventCount: projection?.events.length,
                loadedEventTruncated: projection?.truncated,
                status: projection?.session?.status ?? manifest?.status,
            })
        }

        for (const [rawHarnessId, sessionId] of Object.entries(task.sessionIds)) {
            if ([...result.values()].some((entry) => entry.nestSessionId === sessionId)) continue
            const harnessId = isHarnessId(rawHarnessId) ? rawHarnessId : harnessIdFromProvider(rawHarnessId)
            const projection = this._dynamicEventsBySession.get(sessionId)
            const manifest = result.get(sessionId)
            result.set(sessionId, {
                ...manifest,
                key: sessionId,
                harnessId,
                providerId: projection?.session?.providerId ?? manifest?.providerId,
                providerSessionId: projection?.session?.providerSessionId ?? manifest?.providerSessionId,
                nestSessionId: sessionId,
                model: projection?.session?.model ?? manifest?.model,
                cwd: projection?.session?.cwd ?? manifest?.cwd,
                sessionFile: projection?.session?.sessionFile ?? manifest?.sessionFile,
                sessionRoot: projection?.session?.sessionRoot ?? manifest?.sessionRoot,
                worktreeId: projection?.session?.worktreeId ?? manifest?.worktreeId,
                loadedEventCount: projection?.events.length,
                loadedEventTruncated: projection?.truncated,
                status: projection?.session?.status ?? manifest?.status,
            })
        }

        return [...result.values()].map((session) => {
            const sessionId = session.nestSessionId
            const runtime = sessionId ? this.store.runtimes.runtimeForSession(sessionId) : null
            const usage = sessionId ? this._nestUsageBySession.get(sessionId) : undefined
            return {
                ...session,
                ...(runtime ? { status: runtime.status } : {}),
                ...(usage
                    ? {
                          usage: {
                              costUsd: costFromSessionUsage(usage),
                              costAvailable: hasSessionUsageCost(usage),
                              durationMs: usage.durationMs ?? 0,
                              inputTokens: usage.inputTokens,
                              outputTokens: usage.outputTokens,
                          },
                      }
                    : {}),
            }
        })
    }

    async loadTaskSessionManifest(force = false): Promise<void> {
        // The manifest is a node-scoped read; a guest sees the thread without it.
        if (this.store.isGuest) return
        const task = this.task
        const sessionId = this.primarySessionThreadReference?.sessionId
        if (!task || !sessionId) return
        const sessionIds = [
            ...(task.providerSessions ?? []).map((session) => session.nestSessionId ?? session.providerSessionId),
            ...Object.values(task.sessionIds),
        ].sort()
        const loadKey = `${task.updatedAt ?? ""}:${sessionIds.join("|")}`
        if (!force && this.taskSessionManifest && this.taskSessionManifestLoadKey === loadKey) return
        if (this.taskSessionManifestLoadPromise && this.taskSessionManifestLoadKey === loadKey) {
            if (force) this.taskSessionManifestRefreshRequested = true
            return this.taskSessionManifestLoadPromise
        }

        this.taskSessionManifestLoadKey = loadKey
        runInAction(() => {
            this.taskSessionManifestLoading = true
            this.taskSessionManifestError = null
        })
        const promise = requestWildLarkMethod("wildlark.task.sessions.list", {
            taskId: this.taskId,
            workspaceId: this.workspaceId,
            sessionId,
        })
            .then((manifest) => {
                runInAction(() => {
                    if (this.taskSessionManifestLoadKey !== loadKey) return
                    this.taskSessionManifest = manifest
                    this.taskSessionManifestLoading = false
                })
            })
            .catch((error) => {
                const message = error instanceof Error ? error.message : "Failed to load task session context"
                console.warn("[TaskModel] Failed to load task session context", {
                    taskId: this.taskId,
                    error,
                })
                runInAction(() => {
                    if (this.taskSessionManifestLoadKey !== loadKey) return
                    this.taskSessionManifestError = message
                    this.taskSessionManifestLoading = false
                })
            })
            .finally(() => {
                if (this.taskSessionManifestLoadPromise !== promise) return
                this.taskSessionManifestLoadPromise = null
                const refreshRequested = this.taskSessionManifestRefreshRequested
                this.taskSessionManifestRefreshRequested = false
                if (refreshRequested && !this.disposed) void this.loadTaskSessionManifest(true)
            })
        this.taskSessionManifestLoadPromise = promise
        return promise
    }

    refreshCheckoutSummaries(): Promise<void> {
        if (!this.checkoutSummariesRequested) return Promise.resolve()
        return this.loadCheckoutSummaries(true)
    }

    async loadCheckoutSummaries(force = false): Promise<void> {
        this.checkoutSummariesRequested = true
        if (this.store.isGuest) return
        if (!force && this.checkoutSummaries.length > 0) return
        if (this.checkoutSummariesLoadPromise) {
            if (force) this.checkoutSummariesRefreshRequested = true
            return this.checkoutSummariesLoadPromise
        }

        const promise = (async () => {
            runInAction(() => {
                this.checkoutSummariesError = null
            })
            try {
                if (!this.taskSessionManifest) await this.loadTaskSessionManifest()
                if (this.disposed) return
                const manifest = this.taskSessionManifest
                const original = this.getDirectory("original")
                const working = this.getDirectory("working")
                if (!manifest || !original) throw new Error(this.taskSessionManifestError ?? "Task checkout context is unavailable")

                const result = await loadTaskCheckoutSummaries({
                    workspaceId: this.workspaceId,
                    primaryPath: original.path,
                    selectedPath: working?.path,
                    designatedPath: this.directoryView?.designatedPath,
                    manifest,
                })
                if (this.disposed) return
                runInAction(() => {
                    this.checkoutSummaries = result.checkouts
                    this.checkoutSummaryBaseBranch = result.baseBranch
                    this.checkoutSummaryReads = result.reads
                })
            } catch (error) {
                if (this.disposed) return
                const message = error instanceof Error ? error.message : "Failed to load task checkout summaries"
                console.warn("[TaskModel] Failed to load task checkout summaries", {
                    taskId: this.taskId,
                    error,
                })
                runInAction(() => {
                    this.checkoutSummariesError = message
                })
            }
        })().finally(() => {
            if (this.checkoutSummariesLoadPromise !== promise) return
            this.checkoutSummariesLoadPromise = null
            const refreshRequested = this.checkoutSummariesRefreshRequested
            this.checkoutSummariesRefreshRequested = false
            if (refreshRequested && !this.disposed) void this.loadCheckoutSummaries(true)
        })
        this.checkoutSummariesLoadPromise = promise
        return promise
    }

    private sessionThreadReferenceForSessionId(sessionId: string): SessionThreadReference | null {
        const task = this.task
        if (!task) return null

        const providerSession = task.providerSessions?.find((session) => session.nestSessionId === sessionId)
        if (providerSession) return this.sessionThreadReferenceForProviderSession(providerSession)

        for (const [rawHarnessId, candidateSessionId] of Object.entries(task.sessionIds)) {
            if (candidateSessionId !== sessionId) continue
            return {
                sessionId,
                harnessId: isHarnessId(rawHarnessId) ? rawHarnessId : harnessIdFromProvider(rawHarnessId),
                workspaceId: task.repoId,
            }
        }

        return null
    }

    private sessionThreadReferenceForProviderSession(providerSession: TaskProviderSessionReference): SessionThreadReference {
        return {
            sessionId: providerSession.nestSessionId ?? providerSession.providerSessionId,
            harnessId: isHarnessId(providerSession.harnessId) ? providerSession.harnessId : harnessIdFromProvider(providerSession.providerId),
            providerSessionId: providerSession.providerSessionId,
            workspaceId: this.repoId,
            cwd: providerSession.cwd,
            model: providerSession.model,
        }
    }

    get worktreeRecovery(): TaskWorktreeRecovery | undefined {
        return this.store.productState?.tasks.get(this.taskId)?.worktreeRecovery
    }

    get enabledMcpServerIds(): string[] {
        return this.task?.enabledMcpServerIds ?? []
    }

    get queuedTurns(): QueuedTurn[] {
        return this.task?.queuedTurns ?? []
    }

    async cancelQueuedTurn(queuedTurnId: string): Promise<boolean> {
        if (!this.repoId) return false
        const result = await requestNestMethod("session.turn.cancel", {
            turnId: queuedTurnId,
        })
        return result.cancelled
    }

    setEnabledMcpServerIds(serverIds: string[]): void {
        this.store.tasks.setEnabledMcpServerIds(this.taskId, serverIds)
    }

    getDirectory(purpose: TaskDirectoryPurpose): TaskDirectory | null {
        const repo = this.store.repos.getRepo(this.repoId)
        if (!repo) return null
        return getTaskDirectory(this.directoryView, repo, purpose)
    }

    private syncDirectoryConsumers(directory: TaskDirectory): void {
        if (this._fileBrowser) {
            this._fileBrowser.setDirectory(directory.path, directory.context)
        }
        if (this._contentSearch) {
            this._contentSearch.setDirectory(directory.path, directory.context)
        }
    }

    private publishWorkingDirectorySelection(path: string): void {
        const task = this.task
        const repo = this.store.repos.getRepo(this.repoId)
        if (!task || !repo) return

        const working = taskDirectoryForSelection(task, repo, path)
        const previous = this.directoryView
        const checkout = this.checkoutSummaries.find((candidate) => candidate.path === path)
        const designatedPath = previous?.designatedPath ?? this.checkoutSummaries.find((candidate) => candidate.isDesignated)?.path
        const status = checkout?.status ?? null
        const headCommit = checkout?.headCommit || status?.headCommit
        const designatedPathState =
            working.target === "agent-worktree" ? "active" : previous?.designatedPathState === "active" ? "inactive" : (previous?.designatedPathState ?? "none")

        runInAction(() => {
            this.directoryView = {
                working,
                original: previous?.original ?? getTaskDirectory(null, repo, "original"),
                ...(previous?.diffBaseCommit ? { diffBaseCommit: previous.diffBaseCommit } : {}),
                ...(headCommit ? { taskCommitTarget: { directory: working, headCommit } } : {}),
                status,
                ...(designatedPath ? { designatedPath } : {}),
                designatedPathState,
                selectionMode: "manual",
                manualPath: path,
                manualPathState: "active",
            }
            this.gitStatus = status
            this.taskCommitCount = checkout?.commitsAheadOfBase ?? null
            this.taskCommitCountSourceKey = null
            this.taskCommitCountHeadCommit = null
            this.syncDirectoryConsumers(working)
        })
    }

    private refreshWorkingDirectorySelectionInBackground(path: string): void {
        void (async () => {
            try {
                const productState = this.store.productState
                if (productState) await productState.refreshTask(this.taskId, this.workspaceId)
                if (this.disposed) return

                const observedRefresh = this.directorySelectionRefresh
                const directoryRefresh = observedRefresh?.path === path ? observedRefresh.promise : this.refreshDirectoryView()
                await directoryRefresh
                if (this.disposed) return

                await Promise.all([this.refreshCheckoutSummaries(), this.loadTaskSessionManifest(true)])
            } catch (error) {
                if (this.disposed) return
                console.warn("[TaskModel] Failed to refresh the selected task checkout:", {
                    taskId: this.taskId,
                    path,
                    error,
                })
            }
        })()
    }

    async setRendererWorkingDirectory(path: string | null): Promise<string | null> {
        let selectedPath: string | undefined
        if (path !== null) {
            const inspection = await inspectTaskDirectoryPath(path, this.repoId)
            if (!inspection.ok) return inspection.error
            selectedPath = inspection.path
        }

        const updated = await this.store.tasks.setTaskDirectorySelection(this.taskId, selectedPath ? { mode: "manual", path: selectedPath } : undefined)
        if (!updated) return "The task could not be updated."
        await this.refreshDirectoryView()
        return null
    }

    async setWorkingDirectory(path: string | null): Promise<string | null> {
        if (path === null) return this.setRendererWorkingDirectory(null)

        const requestedPath = path.trim()
        if (!requestedPath) return "Choose a directory."

        const task = this.task
        const continuation = task ? this.continuationProviderSessionForTask(task) : null
        const sessionId = continuation?.nestSessionId
        if (!task || !sessionId) return "This task does not have a resumable session to move to that checkout."

        try {
            const result = await requestWildLarkMethod("wildlark.task.directory.set", {
                taskId: this.taskId,
                workspaceId: this.workspaceId,
                sessionId,
                path: requestedPath,
            })
            this.publishWorkingDirectorySelection(result.workingDir)
            this.refreshWorkingDirectorySelectionInBackground(result.workingDir)
        } catch (error) {
            console.error("[TaskModel] Failed to change task checkout:", error)
            return error instanceof Error ? error.message : "The working directory could not be changed."
        }

        return null
    }

    get hasWorkingChanges(): boolean {
        return this.gitStatus?.hasChanges ?? false
    }

    get aheadCount(): number {
        return this.gitStatus?.ahead ?? 0
    }

    get hasGhCli(): boolean {
        return this.store.repos.getGitInfoSync(this.repoId)?.hasGhCli ?? false
    }

    get pullRequest(): { id?: string; url: string; number?: number; provider: "github" | "gitlab" | "other"; watch?: "active" | "unsupported" } | undefined {
        return this.task?.pullRequest
    }

    get pullRequestAutomation(): TaskPullRequestAutomation | undefined {
        return this.task?.pullRequestAutomation
    }

    private pullRequestAutomationSubmitting = false

    get isSleeping(): boolean {
        return taskIsSleeping(this.task ?? {}, this.store.productState?.attentionNow ?? Date.now())
    }

    async setWatchPR(enabled: boolean): Promise<string | null> {
        return enabled ? this.requestPullRequestAutomation("wildlark.task.pr.automation.set") : this.stopPullRequestAutomation()
    }

    async retryPullRequestAutomation(): Promise<string | null> {
        if (this.pullRequestAutomation?.phase !== "attention") return "PR automation does not need retrying."
        return this.requestPullRequestAutomation("wildlark.task.pr.automation.retry")
    }

    private async requestPullRequestAutomation(method: "wildlark.task.pr.automation.set" | "wildlark.task.pr.automation.retry"): Promise<string | null> {
        if (this.pullRequestAutomationSubmitting) return "PR settings are already being saved."
        if (this.handoffSubmitting || this.pendingHandoff) return "Finish the agent handoff first."
        const link = this.pullRequest
        if (!link?.id || link.provider !== "github" || link.watch !== "active") return "PR automation requires a linked GitHub pull request."
        const sessionId = this.primarySessionThreadReference?.sessionId
        if (!sessionId) return "This task does not have a resumable session."
        this.pullRequestAutomationSubmitting = true
        try {
            const binding = { taskId: this.taskId, workspaceId: this.workspaceId, sessionId }
            const result =
                method === "wildlark.task.pr.automation.set"
                    ? await requestWildLarkMethod(method, {
                          ...binding,
                          options: {
                              mode: "full",
                              permissionMode: this.store.personalSettingsStore?.settings.current.permissionMode ?? "yolo",
                              model: this.model,
                              ...(this.agentGitContext ? { additionalDirs: [this.agentGitContext.baseDir] } : {}),
                              providerOptions: { thinking: this.thinking, fastMode: this.fastMode },
                          },
                      })
                    : await requestWildLarkMethod("wildlark.task.pr.automation.retry", binding)
            await this.store.productState?.refreshTask(this.taskId, this.workspaceId)
            if (result.status === "done-with-gaps") return result.firstErrors[0] ?? "The PR settings could not be fully saved."
            return null
        } catch (error) {
            console.error("[TaskModel] Failed to save PR settings:", error)
            return error instanceof Error ? error.message : "The PR settings could not be saved."
        } finally {
            runInAction(() => {
                this.pullRequestAutomationSubmitting = false
            })
        }
    }

    async stopPullRequestAutomation(): Promise<string | null> {
        const sessionId = this.primarySessionThreadReference?.sessionId
        if (!sessionId) return "This task does not have a resumable session."
        try {
            const result = await requestWildLarkMethod("wildlark.task.pr.automation.stop", {
                taskId: this.taskId,
                workspaceId: this.workspaceId,
                sessionId,
            })
            await this.store.productState?.refreshTask(this.taskId, this.workspaceId)
            return result.status === "done-with-gaps" ? (result.firstErrors[0] ?? "PR automation could not be fully stopped.") : null
        } catch (error) {
            console.error("[TaskModel] Failed to stop PR automation:", error)
            return error instanceof Error ? error.message : "PR automation could not be stopped."
        }
    }

    async unlinkPullRequest(): Promise<string | null> {
        const link = this.pullRequest
        if (!link?.id) return "This task does not have a removable linked request."
        const sessionId = this.primarySessionThreadReference?.sessionId
        if (!sessionId) return "This task does not have a session that can unlink the request."
        try {
            const result = await requestWildLarkMethod("wildlark.task.link.remove", {
                taskId: this.taskId,
                workspaceId: this.workspaceId,
                sessionId,
                linkId: link.id,
            })
            await this.store.productState?.refreshTask(this.taskId, this.workspaceId)
            if (result.status === "done-with-gaps") {
                return result.firstErrors[0] ?? "The request could not be fully unlinked."
            }
            return null
        } catch (error) {
            console.error("[TaskModel] Failed to unlink task request:", error)
            return error instanceof Error ? error.message : "The request could not be unlinked."
        }
    }

    getThreadJson(format: Partial<TaskThreadFormat> = {}): TaskThreadJson | null {
        const task = this.taskWithActivityEvents
        if (!task) return null
        return buildTaskThreadJson(task, format)
    }

    getThreadXml(format: Partial<TaskThreadFormat> = {}): string {
        const task = this.taskWithActivityEvents
        if (!task) return ""
        return buildTaskThreadXml(task, format)
    }

    getThreadMarkdown(format: Partial<TaskThreadFormat> = {}): string {
        const task = this.taskWithActivityEvents
        if (!task) return ""
        return buildTaskThreadMarkdown(task, format)
    }

    private get taskWithActivityEvents(): Task | null {
        const task = this.task
        if (!task) return null
        const events = this.activityEvents.flatMap((event): CodeEvent[] => {
            if (event.type !== "action" || !event.execution.sessionId) return [event]
            const projection = this._dynamicEventsBySession.get(event.execution.sessionId)
            if (!projection || event.id !== `nest-session:${projection.reference.sessionId}`) return [event]
            const actions = syntheticActionsFromProjection(task, projection)
            return actions.length > 0 ? actions : [event]
        })
        return { ...task, events }
    }

    // === Dynamic activity projection ===

    get activityEvents(): CodeEvent[] {
        const task = this.task
        if (!task) return []

        const usedSessions = new Set<string>()
        const actionsBySession = actionEventsBySession(task)
        const baseEvents = task.events.map((event): CodeEvent => {
            if (event.type !== "action") return event
            let hydratedEvent: ActionEvent = event
            if (event.execution.sessionId) {
                const projection = this._dynamicEventsBySession.get(event.execution.sessionId)
                if (projection) {
                    usedSessions.add(event.execution.sessionId)
                    const status = actionStatusFromSession(projection.session?.status)
                    const sessionActions = actionsBySession.get(event.execution.sessionId) ?? [event]
                    hydratedEvent = {
                        ...event,
                        status,
                        completedAt: status === "in_progress" ? undefined : (projection.session?.updatedAt ?? event.completedAt),
                        execution: {
                            ...event.execution,
                            events: eventsForAction(projection, event, sessionActions),
                            modelId: event.execution.modelId ?? projection.session?.model ?? projection.reference.model,
                        },
                    }
                }
            }

            return hydratedEvent
        })

        const syntheticEvents = [...this._dynamicEventsBySession.values()]
            .filter((projection) => !usedSessions.has(projection.reference.sessionId))
            .map((projection) => syntheticActionFromProjection(task, projection))

        return [...baseEvents, ...syntheticEvents].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    }

    async loadDynamicActivity(): Promise<void> {
        if (this._dynamicActivityLoadPromise) return this._dynamicActivityLoadPromise

        const promise = this.loadDynamicActivityOnce()
        this._dynamicActivityLoadPromise = promise
        try {
            await promise
        } finally {
            if (this._dynamicActivityLoadPromise === promise) {
                this._dynamicActivityLoadPromise = null
            }
        }
    }

    private async loadDynamicActivityOnce(): Promise<void> {
        runInAction(() => {
            this.dynamicActivityLoading = true
            this.dynamicActivityError = null
        })

        try {
            const references = this.dynamicSessionReferences()
            if (references.length === 0) {
                runInAction(() => {
                    this._dynamicEventsBySession.clear()
                    this.dynamicActivityReady = true
                    this.dynamicActivityLoading = false
                })
                return
            }

            const projections = await Promise.all(references.map((reference) => this.loadSessionProjection(reference)))
            runInAction(() => {
                this._dynamicEventsBySession.clear()
                for (const projection of projections) {
                    this._dynamicEventsBySession.set(projection.reference.sessionId, projection)
                }
                this.dynamicActivityReady = true
                this.dynamicActivityLoading = false
            })
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to load activity"
            console.warn("[TaskModel] Failed to load dynamic Nest activity", {
                taskId: this.taskId,
                error,
            })
            runInAction(() => {
                this.dynamicActivityError = message
                this.dynamicActivityReady = true
                this.dynamicActivityLoading = false
            })
        }
    }

    private loadSessionProjection(reference: DynamicSessionReference): Promise<DynamicSessionProjection> {
        const promise = this.loadSessionProjectionUntracked(reference)
        trackSessionThreadPrime(reference.sessionId, promise)
        return promise
    }

    private async loadSessionProjectionUntracked(reference: DynamicSessionReference): Promise<DynamicSessionProjection> {
        const [session, loadedEvents] = await Promise.all([
            requestNestMethod("session.read", { sessionId: reference.sessionId })
                .then((result) => result.session)
                .catch((error) => {
                    console.warn("[TaskModel] Failed to read Nest session", {
                        sessionId: reference.sessionId,
                        error,
                    })
                    return undefined
                }),
            this.loadSessionEventWindow(reference),
        ])
        primeSessionThread(reference.sessionId, {
            ...(session ? { session } : {}),
            events: loadedEvents.sessionEvents,
            complete: !loadedEvents.truncated,
        })
        return {
            reference,
            session,
            events: loadedEvents.events,
            sessionEvents: loadedEvents.sessionEvents,
            truncated: loadedEvents.truncated,
        }
    }

    private async loadSessionEventWindow(reference: DynamicSessionReference): Promise<LoadedSessionEvents> {
        const page = await requestNestMethod("session.event.list", {
            sessionId: reference.sessionId,
            limit: SESSION_THREAD_HISTORY_EVENT_LIMIT,
            maxBytes: SESSION_THREAD_HISTORY_BYTE_LIMIT,
        })
        return finalizeLoadedSessionEvents(reference, page.items, page.hasMore)
    }

    private dynamicSessionReferences(excludeSessionIds: ReadonlySet<string> = new Set()): DynamicSessionReference[] {
        const task = this.task
        if (!task) return []

        const references = new Map<string, DynamicSessionReference>()
        for (const providerSession of task.providerSessions ?? []) {
            if (!providerSession.nestSessionId) continue
            if (excludeSessionIds.has(providerSession.nestSessionId)) continue
            const harnessId = isHarnessId(providerSession.harnessId) ? providerSession.harnessId : harnessIdFromProvider(providerSession.providerId)
            references.set(providerSession.nestSessionId, {
                harnessId,
                sessionId: providerSession.nestSessionId,
                providerSessionId: providerSession.providerSessionId,
                cwd: providerSession.cwd,
                model: providerSession.model,
            })
        }

        for (const [rawHarnessId, sessionId] of Object.entries(task.sessionIds)) {
            if (excludeSessionIds.has(sessionId)) continue
            const harnessId = isHarnessId(rawHarnessId) ? rawHarnessId : harnessIdFromProvider(rawHarnessId)
            if (!references.has(sessionId)) {
                references.set(sessionId, { harnessId, sessionId })
            }
        }

        return [...references.values()]
    }

    get statsRefreshKey(): string {
        const task = this.task
        const sessionKey = this.usageSessionIds().join("|")
        return `${sessionKey}@${task?.updatedAt ?? ""}:${task?.events.length ?? 0}`
    }

    async loadNestUsage(): Promise<void> {
        const sessionIds = this.usageSessionIds()
        const loadKey = this.statsRefreshKey
        if (sessionIds.length === 0) {
            runInAction(() => {
                this._nestUsageBySession.clear()
                this.nestUsageError = null
                this.nestUsageLoading = false
                this._nestUsageLoadKey = ""
            })
            return
        }

        if (this._nestUsageLoadPromise && this._nestUsageLoadKey === loadKey) {
            return this._nestUsageLoadPromise
        }

        this._nestUsageLoadKey = loadKey
        const promise = this.loadNestUsageForSessionIds(sessionIds, loadKey)
        this._nestUsageLoadPromise = promise
        try {
            await promise
        } finally {
            if (this._nestUsageLoadPromise === promise) {
                this._nestUsageLoadPromise = null
            }
        }
    }

    private async loadNestUsageForSessionIds(sessionIds: string[], loadKey: string): Promise<void> {
        runInAction(() => {
            this.nestUsageLoading = true
            this.nestUsageError = null
        })

        const results = await Promise.all(sessionIds.map((sessionId) => this.loadNestUsageForSession(sessionId)))
        const failures = results.filter((result) => result.error)
        runInAction(() => {
            if (this.statsRefreshKey !== loadKey) return
            this._nestUsageBySession.clear()
            for (const result of results) {
                if (result.usage) {
                    this._nestUsageBySession.set(result.sessionId, result.usage)
                }
            }
            this.nestUsageError = failures.length > 0 ? `Failed to load usage for ${failures.length} session${failures.length === 1 ? "" : "s"}` : null
            this.nestUsageLoading = false
        })
    }

    private loadNestUsageForSession(sessionId: string): Promise<LoadedSessionUsage> {
        const pending = this._nestUsageReadPromises.get(sessionId)
        if (pending) return pending

        const promise = requestNestMethod("session.usage.read", { sessionId })
            .then(
                (result): LoadedSessionUsage => ({
                    sessionId,
                    usage: result.usage,
                })
            )
            .catch((error): LoadedSessionUsage => {
                console.warn("[TaskModel] Failed to read Nest session usage", {
                    taskId: this.taskId,
                    sessionId,
                    error,
                })
                return { sessionId, error }
            })
            .finally(() => {
                if (this._nestUsageReadPromises.get(sessionId) === promise) {
                    this._nestUsageReadPromises.delete(sessionId)
                }
            })

        this._nestUsageReadPromises.set(sessionId, promise)
        return promise
    }

    private usageSessionIds(): string[] {
        const task = this.task
        if (!task) return []

        const sessionIds = new Set<string>()
        for (const providerSession of task.providerSessions ?? []) {
            if (providerSession.nestSessionId) sessionIds.add(providerSession.nestSessionId)
        }

        for (const sessionId of Object.values(task.sessionIds)) {
            if (isNestSessionId(sessionId)) sessionIds.add(sessionId)
        }

        for (const event of task.events) {
            if (event.type === "action" && isNestSessionId(event.execution.sessionId)) sessionIds.add(event.execution.sessionId)
        }

        return [...sessionIds].sort()
    }

    // === Event models ===

    get events(): EventModel[] {
        const rawEvents = this.activityEvents
        const validIds = new Set<string>()
        const lastIndex = rawEvents.length - 1
        const result = rawEvents.map((e, i) => {
            validIds.add(e.id)
            const cached = this._eventModelCache.get(e.id)
            if (cached) return cached
            const model = this.createEventModel(e, i === lastIndex)
            this._eventModelCache.set(e.id, model)
            return model
        })
        // Drop cache entries for events that no longer exist
        for (const id of this._eventModelCache.keys()) {
            if (!validIds.has(id)) this._eventModelCache.delete(id)
        }
        return result
    }

    private createEventModel(event: CodeEvent, isLast: boolean): EventModel {
        if (event.type === "setup_environment") {
            return new SetupEnvironmentEventModel(this.store, this.taskId, event.id, isLast)
        }
        if (event.type === "snapshot") {
            return new SnapshotEventModel(this.store, this.taskId, event.id, isLast)
        }
        return new ActionEventModel(this.store, this.taskId, event.id, isLast)
    }

    get isWorking(): boolean {
        return this.store.isTaskRunning(this.taskId)
    }

    get stats(): TaskStats {
        const events = this.task?.events ?? []
        const usage = computeTaskUsage(events)
        const eventStats: TaskStats = {
            totalCostUsd: usage.totalCostUsd,
            costAvailable: usage.totalCostUsd > 0,
            durationMs: usage.durationMs ?? 0,
            inputTokens: usage.inputTokens,
            outputTokens: usage.outputTokens,
            loading: this.nestUsageLoading,
            error: this.nestUsageError,
            source: "events",
        }

        const usageSessionIds = this.usageSessionIds()
        if (usageSessionIds.length === 0) return eventStats

        let totalCostUsd = 0
        let durationMs = 0
        let inputTokens = 0
        let outputTokens = 0
        let loadedCount = 0
        let costAvailableCount = 0
        let tokenUsageWithoutCost = false
        for (const sessionId of usageSessionIds) {
            const sessionUsage = this._nestUsageBySession.get(sessionId)
            if (!sessionUsage) continue
            loadedCount++
            totalCostUsd += costFromSessionUsage(sessionUsage)
            if (hasSessionUsageCost(sessionUsage)) {
                costAvailableCount++
            } else if (hasSessionTokenUsage(sessionUsage)) {
                tokenUsageWithoutCost = true
            }
            durationMs += sessionUsage.durationMs ?? 0
            inputTokens += sessionUsage.inputTokens
            outputTokens += sessionUsage.outputTokens
        }

        if (loadedCount === 0) return eventStats

        return {
            totalCostUsd,
            costAvailable: costAvailableCount > 0 && !tokenUsageWithoutCost,
            durationMs,
            inputTokens,
            outputTokens,
            loading: this.nestUsageLoading,
            error: this.nestUsageError,
            source: "nest",
        }
    }

    // === Actions ===

    refreshDirectoryView(): Promise<void> {
        if (this.disposed) return Promise.resolve()

        this.directoryRefreshRequested = true
        if (this.directoryRefreshPromise) return this.directoryRefreshPromise

        // Start in a microtask so synchronous callers share one read. Calls arriving
        // after that read starts request one fresh trailing pass.
        const refreshPromise = Promise.resolve().then(async () => {
            while (this.directoryRefreshRequested && !this.disposed) {
                this.directoryRefreshRequested = false
                await this.refreshDirectoryViewOnce()
            }
        })
        const trackedPromise = refreshPromise.finally(() => {
            if (this.directoryRefreshPromise === trackedPromise) this.directoryRefreshPromise = null
        })
        this.directoryRefreshPromise = trackedPromise
        return trackedPromise
    }

    private async refreshDirectoryViewOnce(): Promise<void> {
        try {
            const task = this.task
            const repo = this.store.repos.getRepo(this.repoId)
            if (!task || !repo) {
                if (this.disposed) return
                runInAction(() => {
                    this.directoryView = null
                    this.gitStatus = null
                    this.taskCommitCount = null
                    this.taskCommitCountSourceKey = null
                    this.taskCommitCountHeadCommit = null
                })
                return
            }

            const gitInfo = await this.store.repos.getGitInfo(this.repoId)
            const previousTarget = this.getDirectory("working")?.target
            const result = await resolveTaskDirectoryView(task, repo, gitInfo?.isGitRepo === true)
            if (this.disposed) return

            const taskCommitTarget = result.taskCommitTarget
            runInAction(() => {
                this.directoryView = result
                this.gitStatus = result.status
                if (!taskCommitTarget) {
                    this.taskCommitCount = null
                    this.taskCommitCountSourceKey = null
                    this.taskCommitCountHeadCommit = null
                }
                this.syncDirectoryConsumers(result.working)
            })
            if (this.disposed) return

            if (taskCommitTarget) {
                let attemptedCount: { sourceKey: string; headCommit: string } | null = null
                try {
                    const taskCommitRange = await resolveTaskCommitRange(taskCommitTarget)
                    if (this.disposed) return
                    if (!taskCommitRange) {
                        runInAction(() => {
                            this.taskCommitCount = null
                            this.taskCommitCountSourceKey = null
                            this.taskCommitCountHeadCommit = null
                        })
                    } else {
                        const commitCountSourceKey = `${taskCommitRange.directory.path}\0${taskCommitRange.upstreamCommit}`
                        const commitCountIsCurrent =
                            this.taskCommitCountSourceKey === commitCountSourceKey && this.taskCommitCountHeadCommit === taskCommitRange.headCommit
                        runInAction(() => {
                            if (!commitCountIsCurrent) {
                                this.taskCommitCount = null
                                this.taskCommitCountHeadCommit = null
                            }
                            this.taskCommitCountSourceKey = commitCountSourceKey
                        })
                        if (!commitCountIsCurrent) {
                            attemptedCount = {
                                sourceKey: commitCountSourceKey,
                                headCommit: taskCommitRange.headCommit,
                            }
                            if (taskCommitRange.headCommit === taskCommitRange.upstreamCommit) {
                                runInAction(() => {
                                    if (this.taskCommitCountSourceKey === commitCountSourceKey) {
                                        this.taskCommitCount = 0
                                        this.taskCommitCountHeadCommit = taskCommitRange.headCommit
                                    }
                                })
                            } else {
                                const count = await gitApi.getCommitCount({
                                    ...taskCommitRange.directory.context,
                                    directory: taskCommitRange.directory.path,
                                    from: taskCommitRange.upstreamCommit,
                                    to: taskCommitRange.headCommit,
                                })
                                if (this.disposed) return
                                runInAction(() => {
                                    if (this.taskCommitCountSourceKey === commitCountSourceKey) {
                                        this.taskCommitCount = count
                                        this.taskCommitCountHeadCommit = taskCommitRange.headCommit
                                    }
                                })
                            }
                        }
                    }
                } catch (error) {
                    if (this.disposed) return
                    console.warn("[TaskModel] Failed to count task commits:", {
                        taskId: this.taskId,
                        error,
                    })
                    runInAction(() => {
                        this.taskCommitCount = null
                        if (attemptedCount && this.taskCommitCountSourceKey === attemptedCount.sourceKey) {
                            // Cache failed counts too so unsupported or unauthorized ranges do not retry on every refresh.
                            this.taskCommitCountHeadCommit = attemptedCount.headCommit
                        } else {
                            this.taskCommitCountSourceKey = null
                            this.taskCommitCountHeadCommit = null
                        }
                    })
                }
            }
            if (previousTarget && previousTarget !== result.working.target) {
                console.debug("[TaskModel] Task directory changed:", {
                    taskId: this.taskId,
                    from: previousTarget,
                    to: result.working.target,
                    workDir: result.working.path,
                })
            }
            if (result.working.target === "agent-worktree" && previousTarget !== "agent-worktree" && !this.trackedAgentGitTarget) {
                this.trackedAgentGitTarget = true
                track("task_git_target_agent_worktree", { taskId: this.taskId })
            }
        } catch (err) {
            if (this.disposed) return
            console.error("[TaskModel] Failed to refresh task directory:", err)
            const original = this.getDirectory("original")
            runInAction(() => {
                this.directoryView = null
                this.gitStatus = null
                this.taskCommitCount = null
                this.taskCommitCountSourceKey = null
                this.taskCommitCountHeadCommit = null
                if (original) this.syncDirectoryConsumers(original)
            })
        }
    }
}
